import { useCallback } from 'react';
import { useLazyQuery } from '@apollo/client';
import { useDispatch, useSelector, useStore } from 'react-redux';

import {
  actionSetCurrentWorkOrderOp,
  actionSetLaborTickets,
} from 'lib/actions';
import { GET_CURRENT_MACHINE_LABOR_TICKET } from 'lib/api/queries';
import { getLaborTicketWhereForMachine } from 'lib/selectors/getLaborTicketWhereForMachine';
import { getMachine } from 'lib/selectors/getMachine';
import { getPostLaborUpdatesOnNats } from 'lib/selectors/getPostLaborUpdatesOnNats';
import { getIsApmERPJobFlow } from 'lib/selectors/getIsApmERPJobFlow';
import { workOrderOpFromLaborTicket } from 'lib/utils/workOrderOpFromLaborTicket';

const useResyncMachineLaborTickets = () => {
  const dispatch = useDispatch();
  const store = useStore();
  const machineRef = useSelector(getMachine)?.machineRef;
  const [requestCurrentMachineLaborTicket] = useLazyQuery(
    GET_CURRENT_MACHINE_LABOR_TICKET,
    {
      fetchPolicy: 'network-only',
    }
  );

  return useCallback(async () => {
    if (!machineRef) {
      return [];
    }

    // Read the where when the resync runs, not when the callback was made: it
    // depends on the signed-in operator, which a submit can change right before
    // this is awaited (sign-in / sign-out).
    const where = getLaborTicketWhereForMachine(store.getState());
    const { data } = await requestCurrentMachineLaborTicket({
      variables: { where },
    });
    const laborTickets = data?.erpLaborTickets || [];

    // The operator may have changed while the request was in flight, and the
    // subscription may already have delivered the new operator's ticket: a
    // response fetched for the previous where must not overwrite it.
    if (getLaborTicketWhereForMachine(store.getState()) !== where) {
      return laborTickets;
    }
    dispatch(actionSetLaborTickets(laborTickets));

    // NATS labor flow: the current operation comes from the current ticket, as
    // the subscription sets it, so a resync that lands on a different job
    // doesn't leave the previous job current until the subscription catches up.
    const state = store.getState();
    if (getPostLaborUpdatesOnNats(state) && !getIsApmERPJobFlow(state)) {
      dispatch(
        actionSetCurrentWorkOrderOp(workOrderOpFromLaborTicket(laborTickets[0]))
      );
    }

    return laborTickets;
  }, [dispatch, machineRef, store, requestCurrentMachineLaborTicket]);
};

export default useResyncMachineLaborTickets;
